import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, RotateCcw, Download, Paperclip, ExternalLink } from "lucide-react";
import { API } from "../../lib/api";

export default function ApprovalPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [approver, setApprover] = useState("");
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/approval/${token}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || "Invalid link"); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [token]);

  const submit = async (status) => {
    if (!approver.trim()) { setError("Please enter your name so we can log the decision."); return; }
    if (!comments.trim()) { setError("Please provide your feedback or remarks before submitting."); return; }
    setBusy(status); setError("");
    try {
      const r = await fetch(`${API}/public/approval/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, approver, comments }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Could not submit");
      setDone(status);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(""); }
  };

  if (error && !data) return (
    <div className="public-page">
      <div className="public-card">
        <img src="/pinaki_logo.png" alt="Pinaki Solutions" className="public-brand-logo" />
        <h1>Link unavailable</h1>
        <p className="muted">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="public-page"><div className="public-card"><p className="muted">Loading approval…</p></div></div>;

  if (done) return (
    <div className="public-page">
      <div className="public-card">
        <img src="/pinaki_logo.png" alt="Pinaki Solutions" className="public-brand-logo" />
        <div className={`public-status ${done === "Approved" ? "green" : "amber"}`}>
          {done === "Approved" ? <CheckCircle2 size={44} /> : done === "Rejected" ? <XCircle size={44} /> : <RotateCcw size={44} />}
        </div>
        <h1>{done === "Approved" ? "Thank you — Design Approved" : "Feedback recorded"}</h1>
        <p className="muted">
          {done === "Approved"
            ? "Your approval has been automatically recorded in the CRM and Production has been unlocked. The manufacturing team will commence production shortly."
            : "Your feedback has been routed to the design team. They will revise the artwork and share the updated version with you shortly."}
        </p>
      </div>
    </div>
  );

  const o = data.order;
  const d = data.design;
  const docs = data.documents || [];

  return (
    <div className="public-page">
      <div className="public-card wide">
        <img src="/pinaki_logo.png" alt="Pinaki Solutions" className="public-brand-logo" />
        <div className="small-label">CLIENT DESIGN APPROVAL · #{o.order_id}</div>
        <h1>{o.product}</h1>
        <p className="muted">Design version <b>V{d.version}</b> by {d.designer} · {new Date(d.created_at).toLocaleDateString()}</p>
        <div className="facts">
          <div><small>Client Company</small><b>{o.client_name}</b></div>
          <div><small>Quantity</small><b>{(o.quantity || 0).toLocaleString()} units</b></div>
          <div><small>Order value</small><b>₹{(o.total_value || 0).toLocaleString()}</b></div>
          <div><small>Required delivery</small><b>{o.required_delivery_date}</b></div>
        </div>

        {/* Attached Artwork & Design Files */}
        {docs.length > 0 && (
          <div style={{ marginTop: 18, padding: 14, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <Paperclip size={15} color="#0284c7" />
              <b style={{ fontSize: 12, color: "#334155", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Attached Artwork Files ({docs.length})
              </b>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {docs.map((doc) => {
                const downloadUrl = `${API}/public/approval/${token}/documents/${doc.document_id}/download`;
                return (
                  <div
                    key={doc.document_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>🎨</span>
                      <div>
                        <b style={{ fontSize: 12.5, color: "#0f172a" }}>{doc.name}</b>
                        <small className="muted" style={{ display: "block", fontSize: 11 }}>{doc.category || "Design File"}</small>
                      </div>
                    </div>
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="outline-btn compact"
                      style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, textDecoration: "none" }}
                    >
                      <Download size={13} /> View / Download Artwork
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {d.comments && <div className="public-notes" style={{ marginTop: 16 }}><b>Designer notes & instructions:</b><p>{d.comments}</p></div>}
        {data.decided && <div className="waiting" data-testid="approval-already-decided">This design version has already been reviewed. Submitting again will log an additional decision.</div>}

        <div className="stack top-gap">
          <label>
            Your Name (Client Contact / Authorized Signer) *
            <input value={approver} onChange={(e) => setApprover(e.target.value)} placeholder="Full name for our official sign-off records" data-testid="public-approver-input" required />
          </label>
          <label>
            Approval Comments / Feedback
            <span style={{ color: "#dc2626", marginLeft: 3, fontWeight: 700 }}>*</span>
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Required — please enter your feedback, acceptance notes, or revision instructions..."
              data-testid="public-comments-input"
              required
              style={comments.trim() === "" && busy !== "" ? { borderColor: "#dc2626" } : {}}
            />
          </label>
          {error && <div className="error" data-testid="public-approval-error">{error}</div>}
          <div className="row-3" style={{ marginTop: 8 }}>
            <button className="primary-btn" style={{ background: "#059669", borderColor: "#059669", fontSize: 13.5, padding: "12px 20px" }} data-testid="public-approve-button" disabled={busy !== ""} onClick={() => submit("Approved")}>
              <CheckCircle2 size={16} /> {busy === "Approved" ? "Submitting Approval…" : "Approve Design"}
            </button>
            <button className="outline-btn" style={{ borderColor: "#d97706", color: "#b45309" }} data-testid="public-revision-button" disabled={busy !== ""} onClick={() => submit("Revision Required")}>
              <RotateCcw size={15} /> Request Revision
            </button>
            <button className="outline-btn danger" data-testid="public-reject-button" disabled={busy !== ""} onClick={() => submit("Rejected")}>
              <XCircle size={15} /> Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
